import {
	DRIVER_NOW_STATUSES,
	DRIVER_SORT_KEYS,
	type Driver,
	type DriverSortKey,
	type MeResponse,
	PAGE_SIZE_MAX,
	type TransportProvider,
	VEHICLE_SORT_KEYS,
	VEHICLE_STATUSES,
	VEHICLE_TYPES,
	type Vehicle,
	type VehicleSortKey,
} from "@seagames/contracts";
import { type ColumnDef, DataTable, IconButton, Icons, ListHeaderButton, ListToolbar, PrimaryCell, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { clusterOptionsOptions, driversListOptions, providersListOptions, vehiclesListOptions } from "../../lib/api/transport-fleet";
import { searchText } from "../../lib/list-page/list-search";
import { type SearchNavigate, useListPage } from "../../lib/list-page/use-list-page";
import { canManageProviders, canWriteFleet, isProvider } from "../../lib/transport/access";
import { PinIcon } from "../transport/icons";
import { Avatar } from "../users/ui";
import { ClustersModal } from "./clusters-modal";
import { DriverFormModal, type DriverFormTarget } from "./driver-form-modal";
import { DropoffsModal } from "./dropoffs-modal";
import { useCallingCodes } from "./hooks";
import { CertifiedTypes, DriverNowChip, InactiveChip, PhoneLink, RampPill, ShiftText, VehicleStatusChip, VehicleTypeIcon, VehicleTypeLabel, useVehicleTypeLabel, useWeekdayName } from "./parts";
import { PlacesModal } from "./places-modal";
import { ProviderFormModal, type ProviderFormTarget } from "./provider-form-modal";
import { SettingsModal } from "./settings-modal";
import { VehicleFormModal, type VehicleFormTarget } from "./vehicle-form-modal";

/**
 * Transportation → Fleet (her "Drivers & vehicles", `#/tp/fleet`, PLAN §4):
 * Drivers | Vehicles (| Companies for SUPER_ADMIN), each an offset list
 * whose page, search, chips and sort live in the URL. The set-up sheets —
 * Clusters & drive times, Places, Contingent drop-offs, Transport settings —
 * open from the header. A provider login sees its own fleet, read-only
 * (scoped in SQL by `providerOwns`).
 *
 * S4's route (`logistics.transportation.fleet.tsx`) spreads
 * `fleetSearchShape` into its `validateSearch` and passes `Route.useNavigate()`.
 */

const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined);

export const FLEET_TABS = ["drivers", "vehicles", "providers"] as const;
export type FleetTab = (typeof FLEET_TABS)[number];

export const fleetSearchShape = {
	fleet: lenient(z.enum(FLEET_TABS)),
	page: lenient(z.coerce.number().int().min(1)),
	page_size: lenient(z.coerce.number().int().min(1).max(PAGE_SIZE_MAX)),
	search: lenient(searchText()),
	sort: lenient(z.union([z.enum(DRIVER_SORT_KEYS), z.enum(VEHICLE_SORT_KEYS), z.literal("name")])),
	dir: lenient(z.enum(["asc", "desc"])),
	now: lenient(z.enum(DRIVER_NOW_STATUSES)),
	cluster_id: lenient(z.string().uuid()),
	vehicle_type: lenient(z.enum(VEHICLE_TYPES)),
	vehicle_status: lenient(z.enum(VEHICLE_STATUSES)),
};
export const fleetSearchSchema = z.object(fleetSearchShape);
export type FleetSearch = z.infer<typeof fleetSearchSchema>;

type Session = Pick<MeResponse, "active_role" | "group">;

export interface FleetViewProps {
	session: Session;
	/** Drop-offs and transport settings are per games; the fleet itself is shared by both. */
	gamesId: string;
	search: FleetSearch;
	navigate: SearchNavigate<FleetSearch>;
}

type SetupSheet = "clusters" | "places" | "dropoffs" | "settings";

const isDriverSort = (v: string | undefined): v is DriverSortKey => (DRIVER_SORT_KEYS as readonly string[]).includes(v ?? "");
const isVehicleSort = (v: string | undefined): v is VehicleSortKey => (VEHICLE_SORT_KEYS as readonly string[]).includes(v ?? "");

const RESET: Partial<FleetSearch> = {
	page: undefined,
	page_size: undefined,
	search: undefined,
	sort: undefined,
	dir: undefined,
	now: undefined,
	cluster_id: undefined,
	vehicle_type: undefined,
	vehicle_status: undefined,
};

export function FleetView({ session, gamesId, search, navigate }: FleetViewProps) {
	const { t } = useTranslation("transportFleet");
	const canWrite = canWriteFleet(session);
	const provider = isProvider(session);
	const canProviders = canManageProviders(session);
	const tab: FleetTab = search.fleet === "providers" && canProviders ? "providers" : search.fleet === "vehicles" ? "vehicles" : "drivers";
	const [sheet, setSheet] = useState<SetupSheet | null>(null);
	const [driverForm, setDriverForm] = useState<DriverFormTarget | null>(null);
	const [vehicleForm, setVehicleForm] = useState<VehicleFormTarget | null>(null);
	const [providerForm, setProviderForm] = useState<ProviderFormTarget | null>(null);

	const switchTo = (next: FleetTab) => void navigate({ search: (prev) => ({ ...prev, ...RESET, fleet: next === "drivers" ? undefined : next }), replace: true });

	const add =
		tab === "drivers"
			? { label: t("fleet.addDriver"), onClick: () => setDriverForm({ mode: "create" }) }
			: tab === "vehicles"
				? { label: t("fleet.addVehicle"), onClick: () => setVehicleForm({ mode: "create" }) }
				: { label: t("fleet.addProvider"), onClick: () => setProviderForm({ mode: "create" }) };

	const tabs: { key: FleetTab; label: string }[] = [
		{ key: "drivers", label: t("fleet.tabs.drivers") },
		{ key: "vehicles", label: t("fleet.tabs.vehicles") },
		...(canProviders ? [{ key: "providers" as const, label: t("fleet.tabs.providers") }] : []),
	];

	return (
		<div className="flex min-w-0 flex-col gap-[1rem]">
			{/* Her `.sec__h`: the Drivers | Vehicles segment, the set-up sheets and the add button. */}
			<div className="flex flex-wrap items-center justify-between gap-[0.75rem]">
				<div role="tablist" aria-label={t("fleet.tabs.label")} className="inline-flex gap-[0.25rem] rounded-[0.625rem] bg-gms-soft-2 p-[0.25rem]">
					{tabs.map((x) => {
						const on = x.key === tab;
						return (
							<button
								key={x.key}
								type="button"
								role="tab"
								aria-selected={on}
								onClick={() => !on && switchTo(x.key)}
								className={`inline-flex h-[2.25rem] min-w-[7rem] cursor-pointer items-center justify-center rounded-[0.5rem] px-[1rem] text-[0.875rem] transition-colors outline-none focus-visible:ring-2 focus-visible:ring-gms-navy/40 ${
									on ? "bg-base-100 font-semibold text-gms-navy shadow-[0_1px_2px_rgba(18,20,63,0.12),0_0_0_1px_var(--color-gms-line)]" : "font-medium text-gms-ink-2 hover:text-gms-navy-ink"
								}`}
							>
								{x.label}
							</button>
						);
					})}
				</div>
				{canWrite ? (
					<div className="flex flex-wrap items-center gap-[0.5rem] max-[44.999rem]:w-full">
						<ListHeaderButton icon={<PinIcon />} onClick={() => setSheet("clusters")}>
							{t("fleet.setup.clusters")}
						</ListHeaderButton>
						<ListHeaderButton onClick={() => setSheet("places")}>{t("fleet.setup.places")}</ListHeaderButton>
						<ListHeaderButton onClick={() => setSheet("dropoffs")}>{t("fleet.setup.dropoffs")}</ListHeaderButton>
						<ListHeaderButton icon={<GearIcon />} onClick={() => setSheet("settings")}>
							{t("fleet.setup.settings")}
						</ListHeaderButton>
						{(tab !== "providers" || canProviders) && (
							<ListHeaderButton tone="primary" icon={<Icons.Plus />} onClick={add.onClick} className="max-[44.999rem]:order-first max-[44.999rem]:flex-[1_1_100%]">
								{add.label}
							</ListHeaderButton>
						)}
					</div>
				) : (
					provider && <p className="max-w-[34rem] text-[0.8125rem] text-gms-ink-3">{t("fleet.readOnly")}</p>
				)}
			</div>

			{tab === "drivers" ? (
				<DriversList key="drivers" search={search} navigate={navigate} canWrite={canWrite} onEdit={(d) => setDriverForm({ mode: "edit", driver: d })} />
			) : tab === "vehicles" ? (
				<VehiclesList key="vehicles" search={search} navigate={navigate} canWrite={canWrite} onEdit={(v) => setVehicleForm({ mode: "edit", vehicle: v })} />
			) : (
				<ProvidersList key="providers" search={search} navigate={navigate} onEdit={(p) => setProviderForm({ mode: "edit", provider: p })} />
			)}

			{canWrite && (
				<>
					<DriverFormModal target={driverForm} onClose={() => setDriverForm(null)} />
					<VehicleFormModal target={vehicleForm} onClose={() => setVehicleForm(null)} />
					{sheet === "clusters" && <ClustersModal gamesId={gamesId} onClose={() => setSheet(null)} />}
					{sheet === "places" && <PlacesModal onClose={() => setSheet(null)} />}
					{sheet === "dropoffs" && <DropoffsModal gamesId={gamesId} onClose={() => setSheet(null)} />}
					{sheet === "settings" && <SettingsModal gamesId={gamesId} onClose={() => setSheet(null)} />}
				</>
			)}
			{canProviders && <ProviderFormModal target={providerForm} onClose={() => setProviderForm(null)} />}
		</div>
	);
}

// ── Lists ───────────────────────────────────────────────────────────────────

interface ListProps<Row> {
	search: FleetSearch;
	navigate: SearchNavigate<FleetSearch>;
	canWrite?: boolean;
	onEdit: (row: Row) => void;
}

const EMPTY_FILTERS = { search: "" };
const filterWiring = {
	empty: EMPTY_FILTERS,
	fromUrl: (s: FleetSearch) => ({ search: s.search ?? "" }),
	toUrl: (f: { search: string }) => ({ search: f.search.trim() || undefined }),
};

/** The cluster chips: one per cluster, applied on click (hers). */
function useClusterChips(search: FleetSearch, patch: (p: Partial<FleetSearch>) => void, clusters: { cluster_id: string; name: string }[]) {
	const { t } = useTranslation("transportFleet");
	return {
		kind: "options" as const,
		key: "cluster",
		label: t("fleet.clusterChips"),
		options: clusters.map((c) => ({ value: c.cluster_id, label: c.name })),
		value: search.cluster_id ? [search.cluster_id] : [],
		onChange: (v: string[]) => patch({ cluster_id: v[0] ?? undefined }),
	};
}

function DriversList({ search, navigate, canWrite, onEdit }: ListProps<Driver>) {
	const { t } = useTranslation("transportFleet");
	const { formatNumber } = useUiStrings();
	const weekday = useWeekdayName();
	const callingCodes = useCallingCodes();
	const go = useNavigate();
	const page = useListPage({
		search,
		navigate,
		listOptions: (q) => driversListOptions({ ...q, sort: isDriverSort(q.sort) ? q.sort : "name", status_now: search.now, cluster_id: search.cluster_id }),
		columnsKey: "transport-drivers",
		filters: filterWiring,
		summary: { range: (v) => t("drivers.summary", v), empty: t("drivers.summaryEmpty") },
	});
	const clusters = useClustersFromOptions();
	const openWeek = (d: Driver) => void go({ to: "/logistics/transportation/drivers/$driverId", params: { driverId: d.driver_id } });

	const columns: ColumnDef<Driver, string>[] = [
		{
			key: "name",
			header: t("drivers.table.driver"),
			sortable: true,
			lockVisible: true,
			minWidth: "10.5rem",
			cell: (d) => (
				<span className="flex min-w-0 items-center gap-[0.625rem]">
					<Avatar name={d.name} />
					<PrimaryCell sub={d.provider?.name}>{d.name}</PrimaryCell>
				</span>
			),
		},
		{ key: "phone", header: t("drivers.table.phone"), minWidth: "8rem", cell: (d) => <PhoneLink phone={d.phone} callingCodes={callingCodes} /> },
		{ key: "types", header: t("drivers.table.certified"), minWidth: "4rem", cell: (d) => <CertifiedTypes types={d.vehicle_types} /> },
		{
			key: "vehicles",
			header: t("drivers.table.vehicles"),
			minWidth: "5rem",
			cell: (d) =>
				d.usual_vehicles.length ? (
					// One plate per line: a plate never breaks inside, and the column stays narrow (E2E B2).
					<span className="grid text-[0.8125rem] leading-[1.4] font-medium tracking-[0.02em] text-gms-ink-2">
						{d.usual_vehicles.map((v) => (
							<span key={v.vehicle_id} className="whitespace-nowrap">
								{v.plate}
							</span>
						))}
					</span>
				) : (
					<span className="text-gms-ink-3">—</span>
				),
		},
		{ key: "cluster", header: t("drivers.table.cluster"), sortable: true, minWidth: "5rem", cell: (d) => <span className="text-gms-ink-2">{d.cluster.name}</span> },
		{ key: "shift", header: t("drivers.table.shift"), minWidth: "7rem", cell: (d) => <ShiftText start={d.shift_start} end={d.shift_end} /> },
		{ key: "rest", header: t("drivers.table.restDay"), minWidth: "5rem", cell: (d) => (d.rest_isodow ? weekday(d.rest_isodow) : <span className="text-gms-ink-3">{t("shift.noRestShort")}</span>) },
		{ key: "now", header: t("drivers.table.statusNow"), minWidth: "6rem", cell: (d) => (d.is_active ? <DriverNowChip status={d.status_now} /> : <InactiveChip />) },
		{
			key: "today",
			header: t("drivers.table.tripsToday"),
			minWidth: "5rem",
			cell: (d) => (
				<Link
					to="/logistics/transportation/drivers/$driverId"
					params={{ driverId: d.driver_id }}
					onClick={(e) => e.stopPropagation()}
					title={t("drivers.table.viewSchedule", { name: d.name })}
					className="whitespace-nowrap font-semibold text-gms-navy tabular-nums underline-offset-2 hover:underline"
				>
					{t("drivers.table.schedule", { n: formatNumber(d.trips_today) })}
				</Link>
			),
		},
	];

	return (
		<>
			<ListToolbar
				search={search.search ?? ""}
				onSearch={(v) => page.patch({ search: v || undefined })}
				searchPlaceholder={t("drivers.searchPlaceholder")}
				sort={{
					value: search.sort === "cluster" ? "cluster" : "name",
					options: [
						{ value: "name", label: t("drivers.sort.name") },
						{ value: "cluster", label: t("drivers.sort.cluster") },
					],
					onChange: (v) => page.patch({ sort: v === "name" ? undefined : (v as FleetSearch["sort"]), dir: undefined }),
				}}
				chips={[
					{
						kind: "options",
						key: "now",
						label: t("drivers.statusChips"),
						options: DRIVER_NOW_STATUSES.map((s) => ({ value: s, label: t(`driverNow.${s}`) })),
						value: search.now ? [search.now] : [],
						onChange: (v) => page.patch({ now: (v[0] as FleetSearch["now"]) ?? undefined }),
					},
					useClusterChips(search, page.patch, clusters),
				]}
				onClearAll={() => page.patch({ search: undefined, now: undefined, cluster_id: undefined })}
			/>
			<DataTable<Driver, string>
				{...page.table}
				variant="list"
				columns={columns}
				rowKey={(d) => d.driver_id}
				onRowClick={openWeek}
				empty={page.emptyState({
					filtered: { title: t("drivers.empty.filteredTitle"), description: t("drivers.empty.filteredDescription") },
					none: { title: t("drivers.empty.noneTitle"), description: canWrite ? t("drivers.empty.noneDescription") : t("drivers.empty.noneReadOnly") },
					clearLabel: t("common.clearFilters"),
				})}
				// The row and its "N · schedule" link open the week; the action column only edits.
				rowActions={canWrite ? (d) => <IconButton label={t("drivers.table.edit", { name: d.name })} icon={<Icons.Pencil />} size="xs" variant="row" onClick={() => onEdit(d)} /> : undefined}
			/>
		</>
	);
}

function VehiclesList({ search, navigate, canWrite, onEdit }: ListProps<Vehicle>) {
	const { t } = useTranslation("transportFleet");
	const { formatNumber } = useUiStrings();
	const typeLabel = useVehicleTypeLabel();
	const page = useListPage({
		search,
		navigate,
		listOptions: (q) => vehiclesListOptions({ ...q, sort: isVehicleSort(q.sort) ? q.sort : "vehicle_type", vehicle_type: search.vehicle_type, status: search.vehicle_status, cluster_id: search.cluster_id }),
		columnsKey: "transport-vehicles",
		filters: filterWiring,
		summary: { range: (v) => t("vehicles.summary", v), empty: t("vehicles.summaryEmpty") },
	});
	const clusters = useClustersFromOptions();

	const columns: ColumnDef<Vehicle, string>[] = [
		{ key: "vehicle_type", header: t("vehicles.table.type"), sortable: true, minWidth: "7rem", cell: (v) => <VehicleTypeLabel type={v.vehicle_type} /> },
		{
			key: "plate",
			header: t("vehicles.table.plate"),
			sortable: true,
			lockVisible: true,
			minWidth: "8rem",
			cell: (v) => <b className="text-[0.84375rem] font-semibold tracking-[0.02em] whitespace-nowrap text-gms-navy-ink">{v.plate}</b>,
		},
		{ key: "model", header: t("vehicles.table.model"), minWidth: "10rem", cell: (v) => <PrimaryCell sub={v.provider?.name}>{v.model}</PrimaryCell> },
		{ key: "cluster", header: t("vehicles.table.cluster"), minWidth: "8rem", cell: (v) => <span className="text-gms-ink-2">{v.cluster.name}</span> },
		{ key: "seats", header: t("vehicles.table.seats"), sortable: true, numeric: true, width: "5rem", cell: (v) => <span className="tabular-nums">{formatNumber(v.seats)}</span> },
		{
			key: "remarks",
			header: t("vehicles.table.remarks"),
			minWidth: "11rem",
			cell: (v) => (
				<span className="flex min-w-0 flex-wrap items-center gap-[0.375rem]">
					{v.has_ramp && <RampPill />}
					{v.remarks && <span className="truncate text-[0.8125rem] text-gms-ink-3" title={v.remarks}>{v.remarks}</span>}
				</span>
			),
		},
		{ key: "status", header: t("vehicles.table.status"), minWidth: "7rem", cell: (v) => <VehicleStatusChip status={v.status} /> },
		{ key: "today", header: t("vehicles.table.tripsToday"), numeric: true, width: "6rem", cell: (v) => <span className={`tabular-nums ${v.trips_today ? "" : "text-gms-ink-3"}`}>{formatNumber(v.trips_today)}</span> },
	];

	return (
		<>
			<ListToolbar
				search={search.search ?? ""}
				onSearch={(v) => page.patch({ search: v || undefined })}
				searchPlaceholder={t("vehicles.searchPlaceholder")}
				sort={{
					value: search.sort === "plate" || search.sort === "seats" ? search.sort : "vehicle_type",
					options: [
						{ value: "vehicle_type", label: t("vehicles.sort.type") },
						{ value: "plate", label: t("vehicles.sort.plate") },
						{ value: "seats", label: t("vehicles.sort.seats") },
					],
					onChange: (v) => page.patch({ sort: v === "vehicle_type" ? undefined : (v as FleetSearch["sort"]), dir: v === "seats" ? "desc" : undefined }),
				}}
				chips={[
					{
						kind: "options",
						key: "type",
						label: t("vehicles.typeChips"),
						options: VEHICLE_TYPES.map((v) => ({ value: v, label: typeLabel(v), icon: <VehicleTypeIcon type={v} className="size-[0.875rem]" /> })),
						value: search.vehicle_type ? [search.vehicle_type] : [],
						onChange: (v) => page.patch({ vehicle_type: (v[0] as FleetSearch["vehicle_type"]) ?? undefined }),
					},
					{
						kind: "options",
						key: "status",
						label: t("vehicles.statusChips"),
						options: (["MAINTENANCE", "RETIRED"] as const).map((s) => ({ value: s, label: t(`vehicleStatus.${s}`) })),
						value: search.vehicle_status ? [search.vehicle_status] : [],
						onChange: (v) => page.patch({ vehicle_status: (v[0] as FleetSearch["vehicle_status"]) ?? undefined }),
					},
					useClusterChips(search, page.patch, clusters),
				]}
				onClearAll={() => page.patch({ search: undefined, vehicle_type: undefined, vehicle_status: undefined, cluster_id: undefined })}
			/>
			<DataTable<Vehicle, string>
				{...page.table}
				variant="list"
				columns={columns}
				rowKey={(v) => v.vehicle_id}
				onRowClick={canWrite ? onEdit : undefined}
				rowClassName={(v) => (v.status === "RETIRED" ? "opacity-60" : undefined)}
				empty={page.emptyState({
					filtered: { title: t("vehicles.empty.filteredTitle"), description: t("vehicles.empty.filteredDescription") },
					none: { title: t("vehicles.empty.noneTitle"), description: canWrite ? t("vehicles.empty.noneDescription") : t("vehicles.empty.noneReadOnly") },
					clearLabel: t("common.clearFilters"),
				})}
				rowActions={canWrite ? (v) => <IconButton label={t("vehicles.table.edit", { plate: v.plate })} icon={<Icons.Pencil />} size="xs" variant="row" onClick={() => onEdit(v)} /> : undefined}
			/>
		</>
	);
}

function ProvidersList({ search, navigate, onEdit }: ListProps<TransportProvider>) {
	const { t } = useTranslation("transportFleet");
	const { formatNumber } = useUiStrings();
	const callingCodes = useCallingCodes();
	const page = useListPage({
		search,
		navigate,
		listOptions: (q) => providersListOptions({ ...q, sort: q.sort === "created_at" ? "created_at" : "name" }),
		columnsKey: "transport-providers",
		filters: filterWiring,
		summary: { range: (v) => t("providers.summary", v), empty: t("providers.summaryEmpty") },
	});

	const count = (n: number) => <span className={`tabular-nums ${n ? "" : "text-gms-ink-3"}`}>{formatNumber(n)}</span>;
	const columns: ColumnDef<TransportProvider, string>[] = [
		{ key: "name", header: t("providers.table.company"), sortable: true, lockVisible: true, minWidth: "13rem", cell: (p) => <PrimaryCell sub={p.email ?? undefined}>{p.name}</PrimaryCell> },
		{
			key: "contact",
			header: t("providers.table.contact"),
			minWidth: "11rem",
			cell: (p) => (
				<span className="grid text-[0.8125rem] leading-[1.4]">
					<span className="text-gms-navy-ink">{p.contact_name ?? <span className="text-gms-ink-3">—</span>}</span>
					{p.contact_phone && <PhoneLink phone={p.contact_phone} callingCodes={callingCodes} className="text-[0.75rem]" />}
				</span>
			),
		},
		{ key: "vehicles", header: t("providers.table.vehicles"), numeric: true, width: "6rem", cell: (p) => count(p.vehicle_count) },
		{ key: "drivers", header: t("providers.table.drivers"), numeric: true, width: "6rem", cell: (p) => count(p.driver_count) },
		{ key: "groups", header: t("providers.table.groups"), numeric: true, width: "7rem", cell: (p) => count(p.group_count) },
		{ key: "status", header: t("providers.table.status"), minWidth: "6rem", cell: (p) => (p.is_active ? <span className="tp-st" data-tp-status="ASSIGNED">{t("common.active")}</span> : <InactiveChip />) },
	];

	return (
		<>
			<p className="-mt-[0.25rem] max-w-[52rem] text-[0.8125rem] text-gms-ink-3">{t("providers.intro")}</p>
			<ListToolbar search={search.search ?? ""} onSearch={(v) => page.patch({ search: v || undefined })} searchPlaceholder={t("providers.searchPlaceholder")} onClearAll={() => page.patch({ search: undefined })} />
			<DataTable<TransportProvider, string>
				{...page.table}
				variant="list"
				columns={columns}
				rowKey={(p) => p.transport_provider_id}
				onRowClick={onEdit}
				empty={page.emptyState({
					filtered: { title: t("providers.empty.filteredTitle"), description: t("providers.empty.filteredDescription") },
					none: { title: t("providers.empty.noneTitle"), description: t("providers.empty.noneDescription") },
					clearLabel: t("common.clearFilters"),
				})}
				rowActions={(p) => <IconButton label={t("providers.table.edit", { name: p.name })} icon={<Icons.Pencil />} size="xs" variant="row" onClick={() => onEdit(p)} />}
			/>
		</>
	);
}

// ── Bits ────────────────────────────────────────────────────────────────────

function useClustersFromOptions(): { cluster_id: string; name: string }[] {
	const q = useQuery(clusterOptionsOptions());
	return q.data ?? [];
}

function GearIcon(): ReactNode {
	return (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
			<circle cx="12" cy="12" r="3" />
			<path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
		</svg>
	);
}
